import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Plus, 
  Trash2,
  ArrowLeft,
  Download,
  FileImage,
  Calendar,
  Loader2,
  CheckCircle
} from 'lucide-react';

export default function TemplateBuilderPage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(templateId || '');
  const [templateInfo, setTemplateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const currentDate = new Date();
  const [formData, setFormData] = useState({
    invoice_number: `INV-${currentDate.getFullYear()}${String(currentDate.getMonth() + 1).padStart(2, '0')}-001`,
    invoice_date: currentDate.toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    month: currentDate.toLocaleString('default', { month: 'long' }),
    year: String(currentDate.getFullYear()),
    client_name: '',
    client_email: '',
    client_address: '',
    client_company: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
    tax_rate: 0,
    notes: '',
    custom_fields: {}
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      fetchTemplateInfo(selectedTemplate);
    }
  }, [selectedTemplate]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      // Filter only DOCX templates
      const docxTemplates = response.data.filter(t => 
        t.file_type?.includes('wordprocessingml') || 
        t.file_type?.includes('msword') ||
        t.file_type?.includes('docx')
      );
      setTemplates(docxTemplates);
      
      if (templateId) {
        setSelectedTemplate(templateId);
      }
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateInfo = async (id) => {
    try {
      const response = await api.get(`/templates/${id}`);
      setTemplateInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch template info');
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, rate: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
    }, 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * ((parseFloat(formData.tax_rate) || 0) / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleGenerate = async (format = 'pdf') => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        ...formData,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          rate: parseFloat(item.rate) || 0
        })),
        tax_rate: String(formData.tax_rate)
      };

      const response = await api.post(
        `/templates/${selectedTemplate}/generate?output_format=${format}`,
        payload,
        { responseType: 'blob' }
      );

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${formData.invoice_number}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Invoice downloaded as ${format.toUpperCase()}!`);
    } catch (error) {
      const errorText = error.response?.data?.detail || 'Failed to generate invoice';
      toast.error(errorText);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="template-builder-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/templates')}
            className="h-9 w-9 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title flex items-center gap-2">
              <FileImage className="w-7 h-7 text-[#4F46E5]" />
              Template Invoice Builder
            </h1>
            <p className="page-subtitle">
              Generate invoices using your exact template design
            </p>
          </div>
        </div>
      </div>

      {templates.length === 0 ? (
        <Card className="dashboard-card">
          <CardContent className="py-12">
            <div className="empty-state">
              <div className="empty-state-icon">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <p className="empty-state-title">No DOCX Templates Found</p>
              <p className="empty-state-text">
                Upload a Word document (.docx) template to use the invoice builder.
                Your template should contain placeholders like [INVOICE_NUMBER], [DATE], [CLIENT_NAME], etc.
              </p>
              <Button 
                className="mt-4 bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                onClick={() => navigate('/templates')}
              >
                Upload Template
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Template Selection */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-lg">Select Template</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="Choose your invoice template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {templateInfo && (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Template loaded: {templateInfo.name}
                    </p>
                    {templateInfo.fields && templateInfo.fields.length > 0 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Detected fields: {templateInfo.fields.map(f => f.name).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invoice Details */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#4F46E5]" />
                  Invoice Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="form-group">
                    <Label>Invoice Number</Label>
                    <Input
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      placeholder="INV-001"
                      className="font-mono"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Invoice Date</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Month</Label>
                    <Input
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      placeholder="January"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Client Info */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-lg">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Client Name</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Company</Label>
                    <Input
                      value={formData.client_company}
                      onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                      placeholder="Acme Inc."
                    />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    placeholder="client@example.com"
                  />
                </div>
                <div className="form-group">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    placeholder="123 Business St, City, State 12345"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="dashboard-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Tasks / Line Items</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </CardHeader>
              <CardContent>
                <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th className="w-1/2">Description</th>
                      <th className="w-20">Qty</th>
                      <th className="w-28">Rate</th>
                      <th className="w-28">Amount</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Input
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="Task or service description"
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          />
                        </td>
                        <td className="text-right font-medium text-slate-900">
                          {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0))}
                        </td>
                        <td>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            className="h-8 w-8 p-0 hover:bg-rose-50"
                            disabled={formData.items.length === 1}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="invoice-totals">
                  <div className="invoice-total-row">
                    <span>Subtotal</span>
                    <span>{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="invoice-total-row flex items-center gap-2">
                    <span>Tax</span>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                      className="w-16 h-7 text-xs"
                    />
                    <span className="text-slate-500">%</span>
                    <span className="ml-auto">{formatCurrency(calculateTax())}</span>
                  </div>
                  <div className="invoice-total-row final">
                    <span>Total</span>
                    <span>{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Payment terms, thank you message, etc."
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            <Card className="dashboard-card sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#4F46E5]" />
                  Generate Invoice
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Invoice #</span>
                    <span className="font-mono font-medium">{formData.invoice_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Items</span>
                    <span className="font-medium">{formData.items.length}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">Total</span>
                    <span className="text-lg font-bold text-[#4F46E5]">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => handleGenerate('pdf')}
                    disabled={generating || !selectedTemplate}
                    className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                    data-testid="generate-pdf-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => handleGenerate('docx')}
                    disabled={generating || !selectedTemplate}
                    variant="outline"
                    className="w-full"
                    data-testid="generate-docx-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download DOCX
                  </Button>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-xs text-slate-500">
                    <strong>Tip:</strong> Your template placeholders like [INVOICE_NUMBER], [CLIENT_NAME], 
                    [DATE], [TOTAL] will be replaced with the values you enter above.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Placeholder Guide */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-sm">Supported Placeholders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs space-y-1 font-mono text-slate-600">
                  <p>[INVOICE_NUMBER] or [INV_NO]</p>
                  <p>[DATE] or [INVOICE_DATE]</p>
                  <p>[DUE_DATE]</p>
                  <p>[MONTH] / [YEAR]</p>
                  <p>[CLIENT_NAME]</p>
                  <p>[CLIENT_EMAIL]</p>
                  <p>[CLIENT_ADDRESS]</p>
                  <p>[CLIENT_COMPANY]</p>
                  <p>[SUBTOTAL]</p>
                  <p>[TAX] / [TAX_RATE]</p>
                  <p>[TOTAL]</p>
                  <p>[NOTES]</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
